import api from "../core/http";

/**
 * All reading orders and readings related API calls
 */

/**
 * Get Reading orders list by type
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getAdminReadingOrders = (
  type = null,
  franchise = null,
  published = null,
  sortBy = "id",
  sortByDirection = "desc",
  searchTerm = null,
  allFranchises = false
) => {
  return api
    .get(`/v1/admin/reading-orders`, {
      params: {
        franchise,
        type,
        published,
        sortBy,
        sortByDirection,
        searchTerm,
        allFranchises,
      },
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get CMRO raw scraps grouped by reading order with their match progress.
 */
export const getAdminRawScraps = () => {
  return api.get(`/v1/admin/raw-scraps`).then((res) => res.data);
};

/**
 * Delete every raw scrap in a CMRO reading-order group.
 */
export const deleteAdminRawScraps = (cmroEntityId) => {
  return api
    .delete(`/v1/admin/raw-scraps/${cmroEntityId}`)
    .then((res) => res.data);
};

/**
 * Retry matching all unmatched comics in a CMRO raw scrap group.
 */
export const rematchAdminRawScraps = (cmroEntityId) => {
  return api
    .post(`/v1/admin/raw-scraps/${cmroEntityId}/rematch`)
    .then((res) => res.data);
};

/**
 * Manually link one raw scrap in a CMRO group to an existing comic.
 */
export const matchAdminRawScrap = (cmroEntityId, rawScrapId, comicId) => {
  return api
    .post(`/v1/admin/raw-scraps/${cmroEntityId}/scraps/${rawScrapId}/match`, {
      comicId,
    })
    .then((res) => res.data);
};

/**
 * Create an unpublished reading order from a fully matched CMRO scrap.
 */
export const createAdminReadingOrderFromRawScraps = (cmroEntityId, values) => {
  return api
    .post(`/v1/admin/raw-scraps/${cmroEntityId}/reading-order`, values)
    .then((res) => res.data);
};

/**
 * Get a single reading order
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getAdminReadingOrder = (readingOrderId) => {
  return api
    .get(`/v1/admin/reading-orders/${readingOrderId}`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get comics order for a single reading order
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getAdminReadingOrderComics = (readingOrderId, page = 1) => {
  return api
    .get(`/v1/admin/reading-orders/${readingOrderId}/comics`, {
      params: { page, limit: 500 },
    })
    .then((res) => res.data);
};

/**
 * Update the reading order
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const updateAdminReadingOrderComics = (readingOrderId, values) => {
  return api
    .post(`/v1/admin/reading-orders/${readingOrderId}/comics`, values)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get Comics list
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getAdminComics = (
  franchise = null,
  searchTerm = null,
  sortBy = "id",
  sortByDirection = "desc"
) => {
  return api
    .get(`/v1/admin/comics`, {
      params: {
        searchTerm: encodeURIComponent(searchTerm),
        franchise,
        sortBy,
        sortByDirection,
      },
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get a single comic
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getAdminComic = (comicId) => {
  return api
    .get(`/v1/admin/comics/${comicId}`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Import a comic from a source
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const adminImportComic = (source, url) => {
  return api
    .post(`/v1/admin/comics/import`, { source, url })
    .then((res) => res.data);
};

/**
 * Update a comic
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const adminUpdateComic = (comicId, formData) => {
  return api
    .post(`/v1/admin/comics/${comicId}/update`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res) => res.data);
};

/**
 * Get requests
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const getRequests = () => {
  return api
    .get(`/v1/admin/requests`)
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Get open reading order requests for the admin queue.
 */
export const getAdminReadingOrderRequests = () => {
  return api.get(`/v1/admin/requests`).then((res) => res.data);
};

/**
 * Deny an open reading order request with an explanation.
 */
export const denyAdminReadingOrderRequest = (requestId, denialReason) => {
  return api
    .post(`/v1/admin/requests/${requestId}/deny`, { denialReason })
    .then((res) => res.data);
};

/**
 * Get user-submitted reading-order corrections awaiting admin review.
 */
export const getAdminCorrections = () => {
  return api.get(`/v1/admin/corrections`).then((res) => res.data);
};

/**
 * Apply a correction to its reading order.
 */
export const acceptAdminCorrection = (correctionId) => {
  return api
    .post(`/v1/admin/corrections/${correctionId}/accept`)
    .then((res) => res.data);
};

/**
 * Remove a correction from the review queue without applying it.
 */
export const dismissAdminCorrection = (correctionId) => {
  return api
    .delete(`/v1/admin/corrections/${correctionId}`)
    .then((res) => res.data);
};

/**
 * Update a comic
 * - uses react-query
 * @param {*} type
 * @returns
 */
export const adminPublishReadingOrder = (readingOrderId, values) => {
  return api
    .post(`/v1/admin/reading-orders/${readingOrderId}/publish`, values)
    .then((res) => res.data);
};

/**
 * Add a comic to MY reading order
 * @param {*} readingOrderId
 * @returns
 */
export const addComicInReadingOrder = (readingOrderId, comicId, order) => {
  return api
    .post(`/v1/admin/reading-orders/${readingOrderId}/add-comic`, {
      comicId,
      order,
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Update a comic to MY reading order
 * @param {*} readingOrderId
 * @returns
 */
export const updateComicInReadingOrder = (
  readingOrderId,
  comicId,
  order,
  story = null
) => {
  console.log("updating...", comicId, order, story);
  return api
    .post(`/v1/admin/reading-orders/${readingOrderId}/update-comic`, {
      comicId,
      order,
      ...(story !== null ? { story } : {}),
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Remove a comic to MY reading order
 * @param {*} readingOrderId
 * @returns
 */
export const removeComicInReadingOrder = (readingOrderId, comicId, order) => {
  return api
    .post(`/v1/admin/reading-orders/${readingOrderId}/remove-comic`, {
      comicId,
      order,
    })
    .then((res) => res.data)
    .catch((err) => err);
};

/**
 * Finalise update on my reading order
 * @param {*} readingOrderId
 * @returns
 */
export const updateMyOrder = (readingOrderId) => {
  return api
    .post(`/v1/admin/reading-orders/${readingOrderId}/finalise`)
    .then((res) => res.data)
    .catch((err) => err);
};

// SCREEN RELEASES (movies / show seasons linked to reading orders)

/**
 * Search screen releases (movies / show seasons) by title for the admin picker.
 * @param {string} searchTerm
 * @param {string|null} franchise mcu | dcu | dceu | star_wars
 */
export const getAdminScreenReleases = (searchTerm = "", franchise = null) => {
  return api
    .get(`/v1/admin/screen-releases`, {
      params: {
        ...(searchTerm ? { q: searchTerm } : {}),
        ...(franchise ? { franchise } : {}),
        per_page: 20,
      },
    })
    .then((res) => res.data);
};

/**
 * Screen releases linked to a reading order.
 */
export const getAdminReadingOrderScreenReleases = (readingOrderId) => {
  return api
    .get(`/v1/admin/reading-orders/${readingOrderId}/screen-releases`)
    .then((res) => res.data);
};

/**
 * Link a screen release to a reading order. Resolves with the updated list.
 */
export const attachAdminScreenRelease = (readingOrderId, screenReleaseId) => {
  return api
    .post(`/v1/admin/reading-orders/${readingOrderId}/screen-releases`, {
      screenReleaseId,
    })
    .then((res) => res.data);
};

/**
 * Unlink a screen release from a reading order. Resolves with the updated list.
 */
export const detachAdminScreenRelease = (readingOrderId, screenReleaseId) => {
  return api
    .delete(
      `/v1/admin/reading-orders/${readingOrderId}/screen-releases/${screenReleaseId}`
    )
    .then((res) => res.data);
};

// SUPPORT (in-app chat inbox)

/**
 * Support conversations by status: open | closed | all.
 * Resolves with { data, meta, counts: { open, closed, awaitingReply } }.
 */
export const getAdminSupportConversations = (status = "open", page = 1) => {
  return api
    .get(`/v1/admin/support`, { params: { status, page } })
    .then((res) => res.data);
};

/**
 * A single conversation with its messages and the reader's details.
 */
export const getAdminSupportConversation = (conversationId) => {
  return api.get(`/v1/admin/support/${conversationId}`).then((res) => res.data);
};

/**
 * Answer a conversation (saved + emailed to the reader), optionally closing it.
 */
export const replyAdminSupportConversation = (
  conversationId,
  message,
  close = false
) => {
  return api
    .post(`/v1/admin/support/${conversationId}/reply`, { message, close })
    .then((res) => res.data);
};

/**
 * Open or close a conversation.
 */
export const updateAdminSupportConversationStatus = (
  conversationId,
  status
) => {
  return api
    .patch(`/v1/admin/support/${conversationId}`, { status })
    .then((res) => res.data);
};
